import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlass, X, CaretDown, Check } from '@phosphor-icons/react';

/**
 * SearchableSelect — a lightweight, accessible, type-to-filter dropdown.
 * Drop-in replacement for <select> when the option list is long.
 *
 * Props
 *   options            : [{ value: string, label: string, sublabel?: string, search?: string }]
 *   value              : string  — currently selected value
 *   onChange           : (newValue) => void
 *   placeholder        : string  — text when nothing is selected
 *   testId             : string  — base data-testid (component appends suffixes)
 *   disabled           : boolean
 */
export default function SearchableSelect({
  options = [],
  value = '',
  onChange = () => {},
  placeholder = 'Select…',
  testId = 'searchable-select',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Close on click-outside
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  // Focus the search box when the dropdown opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const blob = [o.label, o.sublabel, o.search].filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [query, options]);

  // Keep highlight valid as filter changes
  useEffect(() => { setHighlight(0); }, [query]);

  const selected = options.find((o) => o.value === value);

  const choose = (opt) => {
    onChange(opt.value);
    setOpen(false);
    setQuery('');
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlight]) choose(filtered[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <div ref={wrapperRef} className="relative w-full" data-testid={testId}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className={`flex items-center w-full text-left rounded-md border border-input bg-white px-3 py-2 text-sm transition-colors hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        data-testid={`${testId}-trigger`}
      >
        <span className={`flex-1 truncate ${selected ? 'text-gray-900' : 'text-gray-400'}`}>
          {selected ? (selected.label + (selected.sublabel ? ` (${selected.sublabel})` : '')) : placeholder}
        </span>
        {value && !disabled && (
          <span
            role="button"
            tabIndex={-1}
            onClick={(e) => { e.stopPropagation(); onChange(''); }}
            className="mr-1 text-gray-400 hover:text-rose-600 cursor-pointer"
            data-testid={`${testId}-clear`}
          >
            <X size={12} />
          </span>
        )}
        <CaretDown size={12} className="text-gray-400" />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute z-50 mt-1 w-full max-h-[300px] overflow-hidden bg-white rounded-md border shadow-lg flex flex-col"
          data-testid={`${testId}-panel`}
        >
          <div className="p-2 border-b sticky top-0 bg-white">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKey}
                placeholder="Type to search…"
                className="w-full text-sm pl-8 pr-2 py-1.5 border rounded outline-none focus:ring-2 focus:ring-emerald-500/30"
                data-testid={`${testId}-search`}
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="py-6 text-center text-xs text-gray-400" data-testid={`${testId}-empty`}>
                No matches for "{query}"
              </div>
            ) : (
              filtered.map((opt, idx) => {
                const isActive = idx === highlight;
                const isSelected = opt.value === value;
                return (
                  <button
                    type="button"
                    key={`${opt.value || 'noid'}-${idx}`}
                    onClick={() => choose(opt)}
                    onMouseEnter={() => setHighlight(idx)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${isActive ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}
                    data-testid={`${testId}-option-${opt.value}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className={`truncate ${isSelected ? 'font-semibold text-emerald-700' : 'text-gray-900'}`}>{opt.label}</div>
                      {opt.sublabel && (
                        <div className="text-[11px] text-gray-500 truncate">{opt.sublabel}</div>
                      )}
                    </div>
                    {isSelected && <Check size={14} className="text-emerald-600" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
